/**
 * The reading screen. One rule, one small table, a few examples, both languages.
 *
 * It records no attempt. Reading is not answering, and logging it as a correct
 * answer would inflate accuracy with answers the user never gave — the same
 * rule that keeps "I already know this" out of the attempt log.
 *
 * The anchor badge is the signature element and the reason neither course is a
 * generic grammar reference: it answers "which of my two languages does this
 * one behave like here?", which is the question a bilingual learner is actually
 * asking. `neither` is styled loudest because those are the lessons with no
 * intuition to lean on — each course supplies its own labels and styling, since
 * the anchors point in opposite directions.
 */
import { AlertTriangle, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';

import { readingOrder, type GrammarCourse, type GrammarLessonView } from './types';

type Props = {
  lesson: GrammarLessonView;
  course: GrammarCourse;
  /** ISO 639-1 code. The user's own language leads when the course explains in it. */
  primaryLang?: string;
  isReviewLesson?: boolean;
  /** What the button says. The same screen ends a re-read and starts a session. */
  startLabel?: string;
  onStart: () => void;
};

export function GrammarLessonScreen({
  lesson,
  course,
  primaryLang,
  isReviewLesson = false,
  startLabel = 'Start the exercises',
  onStart,
}: Props) {
  const read = (text: Parameters<typeof readingOrder>[0]) =>
    readingOrder(text, course, primaryLang);
  const [rulePrimary, ruleSecondary] = read(lesson.rule);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {isReviewLesson ? 'Review lesson' : `Lesson ${lesson.n}`}
          </span>
          <span
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs',
              course.anchorStyle(lesson.anchor),
            )}
          >
            {course.anchorLabel(lesson.anchor)}
          </span>
        </div>
        <h1 className="font-display text-3xl font-semibold">{lesson.title}</h1>
      </header>

      <section className="border-border bg-card rounded-2xl border p-6 shadow-sm">
        <p className="text-lg leading-relaxed">{rulePrimary}</p>
        {ruleSecondary && (
          <p className="border-border text-muted-foreground mt-3 border-t pt-3 text-base leading-relaxed">
            {ruleSecondary}
          </p>
        )}
      </section>

      {lesson.table && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr>
                {lesson.table.headers.map((header, i) => (
                  <th
                    key={i}
                    className="border-border text-muted-foreground border-b pr-4 pb-2 text-xs font-medium tracking-wide uppercase"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lesson.table.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={cn(
                        'border-border border-b py-2 pr-4',
                        j === 0 ? 'text-muted-foreground text-sm' : 'font-display text-lg',
                      )}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lesson.parallel && (
        <section className="border-border bg-muted/40 flex flex-col gap-1 rounded-lg border p-4">
          <span className="font-display text-lg">{lesson.parallel.target}</span>
          {course.aux.map((lang) =>
            lesson.parallel?.aux[lang] ? (
              <span key={lang} className="text-muted-foreground text-sm">
                {lang.toUpperCase()} &nbsp;{lesson.parallel.aux[lang]}
              </span>
            ) : null,
          )}
        </section>
      )}

      {/*
        The relief: "your other language made you do this, and this one does
        not". Styled as good news rather than a warning, because it is the one
        thing a course for a bilingual learner can say that a monolingual one
        cannot, and burying it in body text wastes it.
      */}
      {lesson.relief && (
        <section className="border-success/30 bg-success/5 flex gap-3 rounded-lg border p-4">
          <Sparkles className="text-success mt-0.5 size-5 shrink-0" aria-hidden />
          <div className="flex flex-col gap-2">
            {read(lesson.relief).map((line, i) => (
              <p
                key={i}
                className={cn('text-sm leading-relaxed', i > 0 && 'text-muted-foreground')}
              >
                {line}
              </p>
            ))}
          </div>
        </section>
      )}

      {lesson.trap && (
        <section className="border-destructive/30 bg-destructive/5 flex gap-3 rounded-lg border p-4">
          <AlertTriangle className="text-destructive mt-0.5 size-5 shrink-0" aria-hidden />
          <div className="flex flex-col gap-2">
            {read(lesson.trap).map((line, i) => (
              <p
                key={i}
                className={cn('text-sm leading-relaxed', i > 0 && 'text-muted-foreground')}
              >
                {line}
              </p>
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3">
        {lesson.examples.map((example, i) => (
          <div key={i} className="border-border flex flex-col gap-0.5 border-l-2 pl-3">
            <span className="font-display text-lg">
              <Highlighted text={example.target} spans={example.focus} />
            </span>
            {read(example.aux).map((line, j) => (
              <span key={j} className="text-muted-foreground text-sm">
                {line}
              </span>
            ))}
          </div>
        ))}
      </section>

      <button
        type="button"
        onClick={onStart}
        className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-12 w-full rounded-lg px-4 font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        {startLabel}
      </button>
    </div>
  );
}

/**
 * Renders `text` with `spans` emphasised.
 *
 * Spans arrive as [start, end) offsets resolved at seed time from inline brace
 * markers, so they cannot drift from the text the way hand-written offsets do.
 */
function Highlighted({ text, spans }: { text: string; spans: [number, number][] }) {
  if (spans.length === 0) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  spans.forEach(([start, end], i) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <mark key={i} className="bg-primary/15 text-primary rounded px-0.5 font-semibold">
        {text.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}
